import { DataHandlerDeviceAdmin, DeviceRPi, type Device } from "luxedo-data"
import { LuxedoRPC } from "luxedo-rpc"
import { Toast } from "toaster"
import { get, writable } from "svelte/store"

export namespace MassUpdater {
	type ContextType = {
		devices: Array<Device>
		version: string
		idList: Array<number>
	}

	const CONTEXT_DEFAULT: ContextType = {
		devices: [],
		version: undefined,
		idList: [],
	}

	let interval
	const SSH_PORT = 31001

	// #region    ====================== Store ======================

	const store = writable<ContextType>(CONTEXT_DEFAULT)

	export function subscribe(cb: (ctx: ContextType) => void) {
		return store.subscribe(cb)
	}

	export function reset() {
		clearInterval(interval)
		store.set(CONTEXT_DEFAULT)
	}

	// #endregion ====================== Store ======================
	// #region    ====================== Operation ======================

	/**
	 * Sets a list of devices to update
	 * @param from
	 * @param to
	 */
	export async function triggerUpdate(
		from: number,
		to: number,
		fwVersion: string
	) {
		let idList = getIdListFromRange(from, to)
		store.set({
			devices: [],
			version: fwVersion,
			idList: idList,
		})

		await refreshDevices(true)

		const ids = get(store).devices.map((dev) => dev.id)
		if (ids.length === 0)
			throw RangeError("All the devices in this range are already updated.")

		for (const id of ids) LuxedoRPC.addCustomData("maintain_control_of", id)

		await LuxedoRPC.api.admin.admin_mass_update_devices(ids, fwVersion)

		store.update((ctx) => ({ ...ctx, idList }))

		await initializeListeners()
	}

	export async function addDevice(id: number) {
		const { version } = get(store)

		LuxedoRPC.addCustomData("maintain_control_of", id)
		await LuxedoRPC.api.admin.admin_mass_update_devices([id], version)

		await DataHandlerDeviceAdmin.pull([id])
		const device = DataHandlerDeviceAdmin.get(id)

		store.update((ctx) => ({
			...ctx,
			idList: ctx.idList.concat([id]),
			devices: ctx.devices.concat([device]),
		}))

		await refreshDevices()
		await initializeListeners()
	}

	async function refreshDevices(hideUpdated?: boolean) {
		const { version, idList } = get(store)

		await DataHandlerDeviceAdmin.pull(idList)
		const devicesUnfiltered = DataHandlerDeviceAdmin.getMany(idList)

		// Makes sure the only devices are RaspberyPI devices and have a lower version than the specified update version, then converts to ids
		let devices = devicesUnfiltered.filter(
			(dev) =>
				dev && (dev.typeId === "dev_luxcast" || dev.typeId === "dev_luxedo")
		)

		if (hideUpdated) {
			// @ts-ignore
			devices = devices.filter((dev) => dev.compareVersion(version) < 0)
		} else {
			// @ts-ignore
			if (
				devices.filter((dev) => dev.compareVersion(version) != 0).length === 0
			) {
				clearInterval(interval)
				Toast.success("All devices successfully updated!")
			}
		}

		store.update((ctx) => ({
			...ctx,
			devices,
			idList: devices.map((dev) => dev.id),
		}))
	}

	export async function forceRefreshDevice(id: number) {
		await LuxedoRPC.api.admin.device_remote_tunnel_open(id, SSH_PORT)
		await LuxedoRPC.api.admin.device_remote_tunnel_close(id)
	}

	function initializeListeners() {
		if (interval) clearInterval(interval)
		interval = setInterval(async () => {
			await refreshDevices()
		}, 5000)
	}

	// #endregion ====================== Operation ======================
	// #region    ====================== Utilities ======================

	/**
	 * Gets a list of ids from the specified start number to the specified end number
	 * @param from the start number
	 * @param to the end number
	 * @returns a list of numbers including from and to
	 */
	function getIdListFromRange(from: number, to: number) {
		let idList = []
		let index = from

		while (index <= to) {
			idList.push(index++)
		}

		return idList
	}
	// #endregion ====================== Utilities ======================
}
